import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, timeAgo, fullDate, resolveImg } from "../lib/api";
import { CategoryChip, Badge } from "../components/Chrome";
import { StoryCard } from "../components/StoryUI";
import { ShareCardGenerator } from "../components/ShareCard";
import { useApp } from "../lib/ctx";
import { Bookmark, Share2, Clock, ChevronRight, ExternalLink } from "lucide-react";

const REACTIONS = [
  { key: "like", emoji: "👍" },
  { key: "wow", emoji: "😮" },
  { key: "sad", emoji: "😢" },
  { key: "haha", emoji: "😂" },
  { key: "fire", emoji: "🔥" },
];

export default function Story() {
  const { slug } = useParams();
  const { toggleSave, isSaved, lang } = useApp();
  const [story, setStory] = useState(null);
  const [related, setRelated] = useState([]);
  const [cats, setCats] = useState([]);
  const [reactions, setReactions] = useState({});
  const [showShare, setShowShare] = useState(false);
  const [polls, setPolls] = useState([]);

  useEffect(() => {
    (async () => {
      setStory(null);
      const [s, c] = await Promise.all([
        api.get(`/stories/${slug}`).then(r => r.data),
        api.get("/categories").then(r => r.data),
      ]);
      setStory(s);
      setCats(c);
      setReactions(s.reactions || {});
      const [rel, p] = await Promise.all([
        api.get("/stories", { params: { category: c.find(x => x.id === s.category_id)?.slug, limit: 4 } }).then(r => r.data),
        api.get("/polls").then(r => r.data),
      ]);
      setRelated(rel.filter(x => x.id !== s.id).slice(0, 3));
      setPolls(p.slice(0, 1));
    })();
  }, [slug]);

  if (!story) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  const cat = cats.find(c => c.id === story.category_id);

  const react = async (key) => {
    const r = await api.post(`/stories/${story.id}/react`, { emoji: key });
    setReactions(r.data);
  };

  const share = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(story.title + " — via Newsgram");
    const links = {
      wa: `https://wa.me/?text=${text}%20${url}`,
      fb: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      x:  `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      li: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    if (platform === "copy") { navigator.clipboard.writeText(window.location.href); return; }
    window.open(links[platform], "_blank");
  };

  return (
    <div className={`max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 ${lang === "bn" ? "font-bn" : ""}`} data-testid="story-page">
      <nav className="text-xs text-muted-foreground mb-4 flex items-center gap-1">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <ChevronRight className="w-3 h-3" />
        {cat && <Link to={`/category/${cat.slug}`} className="hover:text-foreground">{cat.name}</Link>}
      </nav>

      <div className="flex gap-2 flex-wrap mb-4">
        {cat && <CategoryChip category={cat} size="lg" />}
        {story.is_breaking && <Badge kind="BREAKING" />}
        {story.correction_note && <Badge kind="UPDATED" />}
        {story.is_sponsored && <Badge kind="SPONSORED" />}
      </div>

      <h1 className="font-display font-extrabold text-3xl sm:text-5xl leading-[1.05]">{story.title}</h1>

      <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-muted-foreground">
        {story.author && <Link to={`/author/${story.author.id}`} className="font-semibold text-foreground hover:underline">By {story.author.name}</Link>}
        <span className="inline-flex items-center gap-1" title={fullDate(story.published_at)}><Clock className="w-3 h-3" />{timeAgo(story.published_at)}</span>
        <span>•</span><span>{story.read_time} min read</span>
      </div>

      {story.cover_image && (
        <figure className="mt-6">
          <img src={resolveImg(story.cover_image)} alt={story.alt_text} className="w-full rounded-3xl object-cover aspect-[16/10]" />
          <figcaption className="text-[11px] text-muted-foreground mt-2">Photo: {story.image_credit}</figcaption>
        </figure>
      )}

      <div className="mt-8 rounded-3xl bg-muted/60 p-6" data-testid="gist-block">
        <div className="text-[11px] font-black uppercase tracking-widest text-signal">The Gist</div>
        <ul className="mt-3 space-y-3">
          {story.gist.map((g, i) => (
            <li key={i} className="flex gap-3 text-base"><span className="font-display font-black text-signal">{i+1}</span><span>{g}</span></li>
          ))}
        </ul>
      </div>

      {story.why_it_matters && (
        <div className="mt-6 rounded-3xl border-l-4 border-signal pl-5 py-3 bg-signal/5">
          <div className="text-[11px] font-black uppercase tracking-widest text-signal">Why it matters</div>
          <p className="mt-1 text-base">{story.why_it_matters}</p>
        </div>
      )}

      {story.whats_next && (
        <div className="mt-6 rounded-3xl border border-border p-5">
          <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">What's next</div>
          <p className="mt-1 text-base">{story.whats_next}</p>
        </div>
      )}

      {story.body && (
        <div className="mt-8 prose prose-sm sm:prose-base max-w-none text-foreground">
          <p className="whitespace-pre-wrap">{story.body}</p>
        </div>
      )}

      {story.correction_note && (
        <div className="mt-6 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4">
          <div className="text-[11px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-400">Correction — {timeAgo(story.corrected_at)}</div>
          <p className="mt-1 text-sm">{story.correction_note}</p>
        </div>
      )}

      {story.sources?.length > 0 && (
        <div className="mt-8">
          <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Sources</div>
          <ul className="mt-2 space-y-1">
            {story.sources.map((s, i) => (
              <li key={i}>
                <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-signal hover:underline" data-testid={`source-${i}`}>
                  {s.publication} <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Reactions */}
      <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="reactions">
        {REACTIONS.map(r => (
          <button key={r.key} onClick={() => react(r.key)} data-testid={`react-${r.key}`}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-border hover:border-signal bg-card">
            <span className="text-lg">{r.emoji}</span>
            <span className="text-xs font-bold">{reactions[r.key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Share */}
      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <button onClick={() => toggleSave(story)} data-testid="save-btn"
          className={`h-10 px-4 rounded-full border font-bold text-sm inline-flex items-center gap-1.5 ${isSaved(story.id) ? "bg-signal text-white border-signal" : "bg-card border-border"}`}>
          <Bookmark className={`w-4 h-4 ${isSaved(story.id) ? "fill-current" : ""}`} /> {isSaved(story.id) ? "Saved" : "Save"}
        </button>
        <button onClick={() => share("wa")} data-testid="share-wa" className="h-10 px-4 rounded-full border border-border bg-card text-sm font-bold">WhatsApp</button>
        <button onClick={() => share("fb")} data-testid="share-fb" className="h-10 px-4 rounded-full border border-border bg-card text-sm font-bold">Facebook</button>
        <button onClick={() => share("x")}  data-testid="share-x"  className="h-10 px-4 rounded-full border border-border bg-card text-sm font-bold">X</button>
        <button onClick={() => share("li")} data-testid="share-li" className="h-10 px-4 rounded-full border border-border bg-card text-sm font-bold">LinkedIn</button>
        <button onClick={() => share("copy")} data-testid="share-copy" className="h-10 px-4 rounded-full border border-border bg-card text-sm font-bold">Copy link</button>
        <button onClick={() => setShowShare(true)} data-testid="open-share-card" className="h-10 px-4 rounded-full bg-foreground text-background text-sm font-bold inline-flex items-center gap-1.5">
          <Share2 className="w-4 h-4" /> Share card
        </button>
      </div>

      {/* Poll */}
      {polls.length > 0 && <PollCard poll={polls[0]} />}

      {/* Related */}
      {related.length > 0 && (
        <div className="mt-12">
          <h3 className="font-display font-extrabold text-xl mb-4">Keep reading</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {related.map(r => <StoryCard key={r.id} story={r} cats={cats} size="sm" />)}
          </div>
        </div>
      )}

      {showShare && <ShareCardGenerator story={story} categoryColor={cat?.color} onClose={() => setShowShare(false)} />}
    </div>
  );
}

const PollCard = ({ poll }) => {
  const [votes, setVotes] = useState(poll.votes || []);
  const [voted, setVoted] = useState(false);
  const total = votes.reduce((a,b)=>a+b, 0) || 1;
  const vote = async (i) => {
    let did = localStorage.getItem("ng-dev-id");
    if (!did) { did = Math.random().toString(36).slice(2); localStorage.setItem("ng-dev-id", did); }
    const r = await api.post(`/polls/${poll.id}/vote`, { option_index: i, device_id: did });
    setVotes(r.data.votes);
    setVoted(true);
  };
  return (
    <div className="mt-10 rounded-3xl border border-border p-5 bg-card" data-testid="poll-card">
      <div className="text-[11px] font-black uppercase tracking-widest text-signal">Quick poll</div>
      <h3 className="font-display font-bold text-xl mt-2">{poll.question}</h3>
      <div className="mt-4 space-y-2">
        {poll.options.map((o, i) => {
          const pct = Math.round((votes[i] || 0) / total * 100);
          return (
            <button key={i} onClick={() => !voted && vote(i)} disabled={voted}
              data-testid={`poll-opt-${i}`}
              className="w-full text-left relative rounded-xl border border-border bg-background p-3 overflow-hidden">
              {voted && <div className="absolute inset-y-0 left-0 bg-signal/20" style={{ width: `${pct}%` }} />}
              <div className="relative flex items-center justify-between">
                <span className="text-sm font-semibold">{o}</span>
                {voted && <span className="text-xs font-bold">{pct}%</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
